import React, { useEffect, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import Creatable from "react-select/creatable";
import DatePicker from "react-datepicker";
import { toast } from "sonner";
import { formatMoneyInput, stringSort } from "utils";
import dayjs from "dayjs";

import { recursiveAPI } from "services";
import txnaudio from "assets/sounds/google_pay-mini.mp3";
import useUniversalStore from "store/universalStore";
import useOffcanvasStore from "store/offcanvasStore";
import { creatableStyle } from "assets/themes";
import { amountRules, nameRules, optionsInt, defaultValues, calculateNextTriggerDate, getAccountId } from "./config";

const RecursiveTxns = (props) => {
  const { editRcTxn } = props;
  const isEdit = !!editRcTxn;
  const { accountId, accounts } = useUniversalStore();
  const { closeOffcanvas, openOffcanvas } = useOffcanvasStore();

  const { control, handleSubmit, setValue, register, reset, formState: { errors, isSubmitting, isValid } } = useForm({
    defaultValues,
    mode: "onBlur"
  });

  const date = useWatch({ control, name: "date" });
  const interval = useWatch({ control, name: "interval" });
  const phone = useWatch({ control, name: "phone" });
  const nextTriggerDate = useWatch({ control, name: "nextTriggerDate" });

  const [isKHATA, setIsKHATA] = useState(true);
  const [multiplier, setMultiplier] = useState(1);
  const [accountOptions, setAccountOptions] = useState([]);

  useEffect(() => {
    if (editRcTxn?.id) {
      if (editRcTxn.get("accountId")) {
        const acc = editRcTxn.get("accountId");
        setValue("accountName", acc.get("name") || "");
        setValue("phone", acc.get("phone") || "");
      }

      const matchedInterval = optionsInt.find(opt => opt.value === editRcTxn.get("interval")) || optionsInt[0].value;

      setValue("amount", Math.abs(editRcTxn.get("amount") || 0));
      setValue("date", editRcTxn.get("date") || dayjs().add(1, "day").startOf("day").toDate());
      setValue("detail", editRcTxn.get("detail") || "");
      setValue("interval", matchedInterval?.value);
      setValue("nextTriggerDate", editRcTxn.get("nextTriggerDate") || null);
    } else {
      setValue("date", dayjs().add(1, "day").startOf("day").toDate());
    }
  }, [editRcTxn]);

  useEffect(() => {
    if (accountId) {
      const account = accounts.find((acc) => acc.id === accountId);
      if (account) {
        setValue("accountName", account.get("name"));
        setValue("phone", account.get("phone"));
      }
      setIsKHATA(true);
    }
    if (window.location.pathname === "/") {
      setIsKHATA(false);
      setValue("accountName", "");
      setValue("phone", "");
    }
  }, [accountId, accounts]);

  useEffect(() => {
    const filtered = accounts?.filter((a) => !a.get("cancelled"));
    const sorted = stringSort(filtered, false, "name");
    setAccountOptions(sorted.map((a) => ({ value: a.get("phone") ?? a.get("name"), label: a.get("name") })));
  }, [accounts]);

  useEffect(() => {
    const newNextDate = calculateNextTriggerDate(date, interval, editRcTxn);
    setValue("nextTriggerDate", newNextDate);
  }, [date, interval, editRcTxn]);

  useEffect(() => {
    if (!nextTriggerDate) return;
    setValue("date", nextTriggerDate);
  }, [interval]);

  useEffect(() => {
    if (interval === 33) {
      setValue("interval", 1);
    }
  }, [date]);

  const onAmountChange = (e, onChange) => {
    const rawValue = e.target.value.replace(/[^0-9.]/g, "");
    const parts = rawValue.split(".");
    let cleanedValue = parts[0];
    if (parts.length > 1) {
      cleanedValue += "." + parts[1].substring(0, 2);
    }

    if (cleanedValue === "") {
      onChange(0);
      return;
    }

    onChange(cleanedValue);
  };

  const onAccountChange = (selectedOption) => {
    if (!selectedOption) {
      setValue("accountName", "");
      setValue("phone", "");
      return;
    }
    setValue("accountName", selectedOption.label, { shouldValidate: true });
    setValue(
      "phone",
      selectedOption.__isNew__
        ? null
        : selectedOption.value === selectedOption.label
          ? null
          : selectedOption.value
    );
  };

  const handleEdit = async (data) => {
    const originalAmount = editRcTxn.get("amount");
    const submitMultiplier = originalAmount < 0 ? -1 : 1;

    await recursiveAPI.editRecursiveTxn(
      editRcTxn.id,
      data.amount * submitMultiplier,
      data.detail,
      data.nextTriggerDate,
      data.interval,
      data.nextTriggerDate
    );

    toast.success("Recurring transaction updated successfully");
    closeOffcanvas();
    reset();
  };

  const handleCreate = async (data) => {
    const finalAccountId = await getAccountId(isKHATA, data.accountName, data.phone, accountId);

    const triggerDate = data.nextTriggerDate ? new Date(data.nextTriggerDate) : new Date();
    triggerDate.setHours(0, 0, 0, 0);

    return await recursiveAPI.addRecursiveTxn(
      finalAccountId,
      data.amount * multiplier,
      data.detail,
      triggerDate,
      data.interval,
      triggerDate
    );
  };

  const onSubmit = async (data) => {
    try {
      if (isEdit) {
        await handleEdit(data);
      } else {
        const result = await handleCreate(data);
        if (result) {
          toast.success("Recurring transaction saved successfully!");
          // updateRefresh("recursive");
          closeOffcanvas();
          new Audio(txnaudio).play();
        }
      }
    } catch (error) {
      toast.error(error.message || "Update failed");
    }
  };

  const inputClass = (name) => `form-control ${errors[name] ? "is-invalid" : ""}`;
  const labelStyle = { fontSize: "0.75rem" };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="row p-0">
      {isEdit && (
        <div className="col-12 mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <div className="col-6">
              <i className="bi bi-arrow-left-circle-fill fs-3 text-app cursor-pointer" onClick={() => openOffcanvas("recursiveTxns")}></i>
            </div>
            <div className="col-6" align="right">
            </div>
          </div>
        </div>
      )}

      <div className="mb-2 mb-md-3">
        <Controller
          name="amount"
          control={control}
          rules={amountRules}
          render={({ field: { value, onChange, ...rest } }) => (
            <div className="form-control border-0 p-0">
              <label className="text-app" style={labelStyle}>Amount</label>
              <input
                {...rest}
                type="text"
                placeholder="Amount"
                className={inputClass("amount")}
                value={formatMoneyInput(value)}
                onChange={(e) => onAmountChange(e, onChange)}
                disabled={isSubmitting || isEdit}
                title={isEdit ? "You cannot edit amount" : undefined}
              />
            </div>
          )}
        />
        {errors.amount && <div className="invalid-feedback d-block">{errors.amount.message}</div>}
      </div>

      {!isKHATA && (
        <div className="mb-2 mb-md-3">
          <Controller
            name="accountName"
            control={control}
            rules={nameRules}
            render={({ field: { value } }) => (
              <div className="form-control border-0 p-0">
                <label className="text-app" style={labelStyle}>Account Name</label>
                <Creatable
                  options={accountOptions}
                  value={value ? { label: value, value: phone } : null}
                  classNamePrefix="rs"
                  isClearable
                  placeholder="Account Name"
                  onChange={onAccountChange}
                  classNames={creatableStyle}
                  className={errors.accountName ? "is-invalid" : value ? "is-valid" : ""}
                  isDisabled={isSubmitting || isEdit}
                />
              </div>
            )}
          />
          {errors.accountName && <div className="invalid-feedback d-block">{errors.accountName.message}</div>}
        </div>
      )}

      <div className="mb-2 mb-md-3 col-5">
        <div className="form-control border-0 p-0">
          <label className="text-app" style={labelStyle}>Repeat Schedule</label>
          <select className="form-control form-select" {...register("interval", { valueAsNumber: true })} >
            {optionsInt.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          
        </div>
      </div>
      <div className="mb-2 mb-md-3 col-3">
        <div className="form-control border-0 p-0">
          <label className="text-app" style={labelStyle}>No. of Times</label>
          <input
              type="text"
              placeholder="∞"
              className={inputClass("repeatTimes")}
              // value={(e) => value}
              // onChange={(e) => setValue("repeatTimes", acc.get("name"))}
              
              disabled={isSubmitting}
            />
        </div>
      </div>
      <div className="mb-2 mb-md-3 col-4">
        <Controller
          name="date"
          control={control}
          rules={{ required: "Start date is required" }}
          render={({ field: { value, onChange } }) => (
            <div className="form-control border-0 p-0">
              <label className="text-app" style={labelStyle}>Start Date</label>
              <DatePicker
                selected={value}
                onChange={onChange}
                dateFormat="dd-MM-yyyy"
                minDate={dayjs().add(1, "day").startOf("day")}
                popperPlacement="bottom-start"
                className="form-control"
                wrapperClassName="w-100"
                disabled={isSubmitting}
              />
            </div>
          )}
        />
        {errors.date && <div className="invalid-feedback d-block">{errors.date.message}</div>}
      </div>

      <div className="mb-5 pb-5 px-2">
        <div className="border-0 p-0">
          <label className="text-app" style={labelStyle}>Detail</label>
          <div className="form-control d-flex align-items-end p-0">
            <textarea
              {...register("detail")}
              placeholder="Details"
              className="form-control border-0 shadow-none"
              rows={2}
              maxLength={50}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </div>
      {/* <div className=" text-muted" style={{ fontSize: "0.85rem" }}>
        {nextTriggerDate ? "Will start on: " + nextTriggerDate.toDateString() : (isEdit ? "N/A" : "")}
      </div> */}

      <div className="position-absolute bottom-0 start-0 end-0">
        {isEdit ? (
          <div className="px-3 mb-3">
            <button
              type="submit"
              className="btn btn-app w-100 py-3 fs-5 fw-bold text-uppercase"
              disabled={isSubmitting || !isValid || errors.amount}
            >
              <span className="me-2">Update</span>
              {isSubmitting ? (
                <span className="spinner-border spinner-border-sm me-2" />
              ) : null}
            </button>
          </div>
        ) : (
          <div className="row mb-3">
            <div className="col-6 d-grid mt-md-0">
              <button
                type="submit"
                className="btn btn-success block py-3"
                disabled={isSubmitting || !isValid}
                onClick={() => setMultiplier(1)}
              >
                <b className="fs-6 fs-md-5 text-uppercase"><i className="fs-4 bi bi-arrow-down-circle-fill"></i> Get Credit</b>
              </button>
            </div>
            <div className="col-6 d-grid mt-md-0">
              <button
                type="submit"
                className="btn btn-danger block py-3"
                disabled={isSubmitting || !isValid}
                onClick={() => setMultiplier(-1)}
              >
                <b className="fs-6 fs-md-5 text-uppercase"><i className="fs-4 bi bi-arrow-up-circle-fill"></i> Give Credit</b>
              </button>
            </div>
          </div>
        )}
      </div>
    </form>
  );
}

export default RecursiveTxns;